































































import { convertRtlStyle, dataURLtoFile } from '@tt4b/ttam-utils';
import { defineComponent, PropType, ref, nextTick, computed } from '@vue/composition-api';
import CropperClass from 'cropperjs';
import defaultAvatar from '@tt4b/ttam-assets/src/images/creative/default-avatar.png';
import AvatarIcon from '@tt4b/ttam-assets/src/svgs/creative/identity_edit_avatar_icon.svg';

const name = 'ttam-avatar-uploader';
type VuiFileType = {
  uid: number;
  raw: File;
  name: string;
  percentage?: number;
  size?: number;
  status: string;
  url?: string;
  type?: string;
};

// 端上头像展示的地方是 49dp, 视频播放完蒙版头像是 64dp，
// 为了保证图片不失真，这里把图片分辨率改成 112*3,避免端上图片模糊的问题
const AVATAR_SIZE = {
  width: 112 * 3,
  height: 112 * 3,
};

export default defineComponent({
  name,
  components: {
    AvatarIcon,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** 图片上传的接口url */
    action: {
      type: String,
      required: true,
    },
    /** 上传的文件字段名 */
    name: {
      type: String,
      default: 'Filedata',
    },
    /** 上传时携带的http头部 */
    headers: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    /** 上传时附带的额外参数 */
    data: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
    /** 接受上传的图片类型 */
    accept: {
      type: String,
      default: 'image/png,image/jpeg',
    },
    /** 显示上传icon */
    hideIcon: {
      type: Boolean,
      default: false,
    },
    componentWidth: {
      type: String,
      default: '550',
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const files = ref<VuiFileType[]>([]);
    const cropper = ref<CropperClass | null>(null);
    const cropperLoading = ref(false);
    const isUploading = ref(false);
    // refs
    const avatarUpload = ref(null);
    const cropperImage = ref<HTMLImageElement>(null);

    const dialogVisible = ref(false);
    const onDialogClose = () => {
      dialogVisible.value = false;
      cropperLoading.value = false;
      isUploading.value = false;
      files.value = [];
      cropper.value?.destroy();
    };

    const backgroundImage = computed(() =>
      convertRtlStyle({
        backgroundImage: `url(${props.value || defaultAvatar})`,
      }),
    );

    const loadImg = (url: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve(img);
        };
        img.src = url;
        img.onerror = reject;
      });

    const initCropper = async (url: string) => {
      const img = await loadImg(url);
      cropperImage.value.src = url;
      const size = Math.min(img.width, img.height);
      cropper.value?.destroy();
      cropper.value = null;
      const minWidth = parseInt(props.componentWidth, 10) - 24 - 24;
      const { default: Cropper } = await import(/* webpackChunkName: "cropperjs" */ 'cropperjs');
      cropper.value = new Cropper(cropperImage.value, {
        aspectRatio: 1,
        zoomOnWheel: false,
        viewMode: 1,
        minContainerWidth: minWidth,
        minContainerHeight: 336,
        data: {
          x: (img.width - size) / 2,
          y: (img.height - size) / 2,
          width: size,
          height: size,
        },

        ready: () => {
          cropperLoading.value = false;
        },
      });
    };
    const onCropperSubmit = () => {
      if (cropperLoading.value) {
        return;
      }

      let croppedCanvas: HTMLCanvasElement | null = null;
      const newCanvas = document.createElement('canvas');
      newCanvas.width = AVATAR_SIZE.width;
      newCanvas.height = AVATAR_SIZE.height;
      if (cropper.value) {
        croppedCanvas = cropper.value.getCroppedCanvas();
        const ctx2d = newCanvas.getContext('2d');
        ctx2d?.drawImage(
          croppedCanvas,
          0,
          0,
          croppedCanvas.width,
          croppedCanvas.height,
          0,
          0,
          AVATAR_SIZE.width,
          AVATAR_SIZE.height,
        );

        const firstFile = files.value[0];
        const file = dataURLtoFile(newCanvas.toDataURL(firstFile.type), firstFile.name);

        files.value = [
          {
            uid: firstFile.uid,
            status: 'ready',
            raw: file,
            type: file.type,
            name: file.name,
          },
        ];

        nextTick(() => {
          avatarUpload?.value?.submit();
        });
      }
    };

    const getRawFiles = (rawFiles: File[]) => {
      emit('get-raw-files');
      dialogVisible.value = true;
      cropperLoading.value = true;

      nextTick(() => {
        const reader = new FileReader();
        reader.onload = e => {
          initCropper(reader.result as string);
        };
        reader.readAsDataURL(rawFiles[0]);
        files.value = Array.from(rawFiles).map(f => ({
          uid: Number(new Date()),
          raw: f,
          name: f.name,
          status: 'ready',
          type: f.type,
        }));
      });
    };
    const beforeUploadCb = () =>
      new Promise(resolve => {
        isUploading.value = true; // 下一步就是开始上传
        resolve(null);
      });

    const successCb = imageFile => {
      isUploading.value = false;
      onDialogClose();
      emit('success', imageFile.data);
    };

    const errorCb = imageFile => {
      isUploading.value = false;
      emit('error', imageFile);
    };

    const removePic = () => {
      emit('remove');
    };

    return {
      componentName: name,
      backgroundImage,
      files,
      cropper,
      isUploading,
      avatarUpload,
      cropperImage,
      dialogVisible,
      defaultAvatar,
      cropperLoading,
      errorCb,
      removePic,
      successCb,
      getRawFiles,
      onDialogClose,
      beforeUploadCb,
      onCropperSubmit,
    };
  },
});
